import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const EmailVerificationChip: React.FC = () => {
  const [isResending, setIsResending] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { user, emailVerified } = useAuth();

  // Don't show chip if email is verified or user is admin
  if (!user || emailVerified || user.permisos === 'admin' || user.permisos === 'superadmin') {
    return null;
  }

  const handleResendEmail = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResending(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: user.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 3000);
      }
    } catch (error) {
      console.error('Error resending verification:', error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="relative">
      {/* Chip */}
      <div 
        className="flex items-center space-x-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-3 py-1.5 rounded-full text-sm border border-yellow-200 dark:border-yellow-800/50 cursor-pointer hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
        onClick={() => setShowTooltip(!showTooltip)}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span className="font-medium">Email sin verificar</span>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showTooltip ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 z-50">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                Verifica tu email
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Para acceder a funciones avanzadas como facturación e integración con Partners.
              </p>
              
              <div className="text-xs text-gray-500 dark:text-gray-500 mb-3 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                {user.email}
              </div>

              <button
                onClick={handleResendEmail}
                disabled={isResending}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-medium py-2 px-3 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? 'Enviando...' : 'Reenviar Email'}
              </button>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                TIP: Mientras tanto, puedes explorar y crear servicios libremente
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailVerificationChip;